import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { InformationalScreen, LoadingModal } from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { Footer, MainContainer, TopHeaderBackArrow } from 'Components/utility';
import { ApiStatus, APP_RETURN_TIMEOUT, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import { useAccount, useApiError } from 'Hooks';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { signIn } from 'Services/auth';
import { registerPatient } from 'Services/patient';
import { type RootState } from 'src/reducers';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';

import { useNewUserValidationSchema, initialState } from './helpers';
import {
  RegisterAccountForm,
  RegisterAdultAccount,
  RegisterMinorAccount,
  RegisterMinorAccountInfo,
  RegisterMinorAccountIntro,
} from './steps';

const mapStateToProps = ({
  env: { deviceId, osType, osVersion, app, appVersion, country, phoneLanguage },
  config: { validation, adultAges },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  app,
  appVersion,
  validation,
  adultAges,
  firstNameLimit: validation?.libreApi.patient.firstName.stringMaxLength,
  lastNameLimit: validation?.libreApi.patient.lastName.stringMaxLength,
  country,
  phoneLanguage,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const CreateNewUser = ({
  adultAges,
  app,
  appVersion,
  country,
  deviceId,
  firstNameLimit,
  lastNameLimit,
  osType,
  osVersion,
  phoneLanguage,
  validation,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const { showApiErrorModal, showNetworkErrorModal } = useApiError();
  const validationSchema = useNewUserValidationSchema({ step: currentPageIndex });
  const {
    showEmailExistsMessage,
    showExistingProUserError,
    showPasswordValidationError,
    showMinorRestrictionMessage,
    validateEntriesLengthAndDisplayError,
    validateIsMinor,
  } = useAccount();

  const formController = useFormik({
    initialValues: initialState,
    validationSchema,
    validateOnBlur: true,
    onSubmit: (values) => handleSubmitRegister(values),
  });
  const { handleSubmit, setValues, setTouched, setErrors, isValid, isSubmitting, values, touched } =
    formController;

  const handleClickSignIn = useCallback(() => {
    /*
      Need to use the redirect method to replaces the current location in browser history.
      When the user clicks the back button on the login page, it should redirect the user back to the app.
    */
    mediator.publish('router:redirect', '/login');
  }, []);

  const validateNameLength = useCallback(
    (firstName: string, lastName: string) => {
      const isValid = validateEntriesLengthAndDisplayError([
        {
          value: firstName,
          msgKey:
            'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.firstNameMaxLength',
          count: firstNameLimit,
        },
        {
          value: lastName,
          msgKey:
            'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.lastNameMaxLength',
          count: lastNameLimit,
        },
      ]);

      return isValid;
    },
    [firstNameLimit, lastNameLimit, validateEntriesLengthAndDisplayError]
  );

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>) => {
      const data = error.response?.data ?? {
        details:
          error.code === ErrorCode.NETWORK_ERROR
            ? ErrorCode.NETWORK_ERROR
            : i18n.t('Global.modals.errorCommunicatingServer.body'),
      };

      notifyApp('adc-webview:create-account', { status: error.status, data });

      switch (error.status) {
        case ApiStatus.CONFLICT: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showExistingProUserError();

            return;
          }

          showEmailExistsMessage(handleClickSignIn);
          break;
        }

        case ApiStatus.BAD_REQUEST: {
          const code = error.response?.data.code;
          const errors = error.response?.data.errors;

          if (code === ApiStatus.API_ERROR) {
            showApiErrorModal();
          } else {
            showPasswordValidationError({ validation }, errors);
          }

          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      handleClickSignIn,
      showApiErrorModal,
      showEmailExistsMessage,
      showExistingProUserError,
      showNetworkErrorModal,
      showPasswordValidationError,
      validation,
    ]
  );

  const handleSubmitRegister = useCallback(
    (register: Register) => {
      return new Promise<void>((resolve, reject) => {
        setLoading(true);

        registerPatient(register, osType, osVersion, app, appVersion, country, phoneLanguage)
          .then(async () => {
            const authSession = await signIn(
              register.email,
              register.password,
              osType,
              osVersion,
              app,
              appVersion,
              deviceId
            );

            store.dispatch({ type: AUTH_SESSION, authSession });

            notifyApp('adc-webview:create-account', { accountCreated: true });

            if (app !== 'llu5') {
              mediator.publish('router:navigate', '/hipaa');

              resolve();

              return;
            }

            notifyApp<AuthSession>('adc-webview:signin', authSession);

            resolve();

            // This timeout is added to give the app time to redirect the user back.
            setTimeout(() => {
              setLoading(false);
            }, APP_RETURN_TIMEOUT);
          })
          .catch((error) => {
            handleApiRequestError(error);
            reject(error);
          })
          .finally(() => {
            setLoading(false);
          });
      });
    },
    [osType, osVersion, app, appVersion, country, phoneLanguage, deviceId, handleApiRequestError]
  );

  const onNextPressed = useCallback(() => {
    const isMinor = validateIsMinor(values.dateOfBirth, adultAges[country] ?? 0);
    // check if names are valid and show modal if not
    const isNameValid = validateNameLength(values.firstName, values.lastName);

    // can't move further if names too long
    if (!isNameValid) {
      return;
    }

    setValues({ ...values, isMinor }, false);

    // not minor, hence regular steps (adult flow ends on the second screen)
    if (!isMinor) {
      if (currentPageIndex === 0 && isNameValid) {
        // need to reset fields manually for validation to work correctly
        setValues({ ...values, email: '', password: '', confirmPassword: '' }, false);
        setTouched({ ...touched, email: false, password: false, confirmPassword: false }, false);
        setCurrentPageIndex(1);

        return;
      }

      return;
    }

    // no <13 yo minors for 'llu5'
    if (app === 'llu5') {
      showMinorRestrictionMessage(adultAges[country] ?? 0);

      return;
    }

    // user is minor, handle another flow: show minor related info
    if (currentPageIndex === 0) {
      setCurrentPageIndex(2);

      return;
    }

    // move from info, clean up fields
    if (currentPageIndex === 2) {
      // need to reset fields manually for validation to work correctly
      setValues({ ...values, guardianFirstName: '', guardianLastName: '' }, false);
      setTouched({ ...touched, guardianFirstName: false, guardianLastName: false }, false);
      setCurrentPageIndex(3);

      return;
    }

    // handle next step
    // -- need to reset fields manually for validation to work correctly
    setValues({ ...values, email: '', password: '', confirmPassword: '' }, false);
    setTouched({ ...touched, email: false, password: false, confirmPassword: false }, false);
    setCurrentPageIndex(4);
  }, [
    adultAges,
    app,
    country,
    currentPageIndex,
    showMinorRestrictionMessage,
    validateIsMinor,
    validateNameLength,
    setValues,
    setTouched,
    touched,
    values,
  ]);

  const handleBackPressed = useCallback(() => {
    switch (currentPageIndex) {
      case 1:
      case 2:
        setCurrentPageIndex(0);
        break;
      case 3:
      case 4:
        setCurrentPageIndex(currentPageIndex - 1);
        break;
      default:
        mediator.publish('router:navigate', '/login');
    }

    // fix for formik remembering state from before pressing BACK arrow
    setErrors({});
  }, [currentPageIndex, setErrors]);

  return (
    <MainContainer>
      {loading && <LoadingModal aria-label="Loading" />}
      <InformationalScreen
        topNavigationProps={{
          title: i18n.t('CreateAccountPersonalInfo.title'),
          animatedTitle: true,
          buttonStart: {
            element: <TopHeaderBackArrow testID="*** add missing id ***" />,
            label: 'Back',
            onPress: handleBackPressed,
          },
        }}
        index={currentPageIndex}
      >
        <InformationalScreen.Page
          Content={<RegisterAccountForm formikForm={formController} />}
          Footer={
            <Footer
              padding="$4"
              buttonLabel={i18n.t(
                'CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.links.next.label'
              )}
              onButtonPress={onNextPressed}
              buttonTestID="RegisterPersonalInfo.nextButton"
              secondarySectionLabel={i18n.t(
                'CreateAccountPersonalInfo.content.footerSignIn.primaryText'
              )}
              onSecondaryButtonPress={handleClickSignIn}
              secondaryButtonTestID="RegisterPersonalInfo.signIn"
              secondaryButtonLabel={i18n.t(
                'CreateAccountPersonalInfo.content.footerSignIn.links.signIn.label'
              )}
              disabled={
                !values.firstName ||
                !values.lastName ||
                !values.dateOfBirth ||
                !isValid ||
                isSubmitting
              }
            />
          }
        />

        <InformationalScreen.Page
          Content={
            <RegisterAdultAccount
              formikForm={formController}
              identities={validation?.identity}
              onValidate={setMeetsPasswordRequirements}
            />
          }
          Footer={
            <Footer
              padding="$4"
              buttonLabel={i18n.t(
                'CreateAccountAccountInfo.content.createAccountAccountInfoForm.links.next.label'
              )}
              onButtonPress={handleSubmit}
              buttonTestID="RegisterAccountInfo.nextButton"
              secondarySectionLabel={i18n.t(
                'CreateAccountAccountInfo.content.footerSignIn.primaryText'
              )}
              onSecondaryButtonPress={handleClickSignIn}
              secondaryButtonTestID="RegisterAccountInfo.signIn"
              secondaryButtonLabel={i18n.t(
                'CreateAccountAccountInfo.content.footerSignIn.links.signIn.label'
              )}
              disabled={
                !values.email ||
                !values.password ||
                !values.confirmPassword ||
                !isValid ||
                isSubmitting ||
                !meetsPasswordRequirements
              }
            />
          }
        />

        <InformationalScreen.Page
          Content={RegisterMinorAccountIntro}
          Footer={
            <Footer
              padding="$4"
              buttonLabel={i18n.t('Global.microcopy.common.continue')}
              onButtonPress={() => {
                onNextPressed();
              }}
              buttonTestID="CreateAccountIntro.continue-button"
              disabled={false}
            />
          }
        />

        <InformationalScreen.Page
          Content={<RegisterMinorAccountInfo formikForm={formController} />}
          Footer={
            <Footer
              padding="$4"
              buttonLabel={i18n.t(
                'CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.links.next.label'
              )}
              onButtonPress={() => {
                onNextPressed();
              }}
              buttonTestID="CreateAccountPersonalInfo.nextButton"
              disabled={
                !values.guardianFirstName || !values.guardianLastName || !isValid || isSubmitting
              }
            />
          }
        />

        <InformationalScreen.Page
          Content={
            <RegisterMinorAccount
              formController={formController}
              identities={validation?.identity}
              onValidate={setMeetsPasswordRequirements}
            />
          }
          Footer={
            <Footer
              padding="$4"
              buttonLabel={i18n.t(
                'CreateAccountAccountInfo.content.createAccountAccountInfoForm.links.next.label'
              )}
              onButtonPress={handleSubmit}
              buttonTestID="CreateAccountAccountInfo.nextButton"
              disabled={
                !values.email ||
                !values.password ||
                !values.confirmPassword ||
                !isValid ||
                isSubmitting ||
                !meetsPasswordRequirements
              }
            />
          }
        />
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(CreateNewUser);
